/**
 * The one place the dashboard talks to the API.
 *
 * Everything the server requires of a browser client lives here — the
 * organization header, the problem+json envelope, the meaning of a 401 — so a
 * screen cannot forget one of them.
 */

import { explainRefusal } from "../roles";
import type { CursorPage } from "./types";

/** RFC 9457. `type` is the only field to branch on; the prose may be reworded. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  request_id?: string;
  errors?: Array<{ field: string; code: string; message: string }>;

  /**
   * Present on 403: the capability this credential lacks.
   *
   * Served since the capability model shipped and read by nothing until now —
   * it was not even declared here, so every refusal rendered as the raw
   * `detail`, which reads `requires the campaigns:send capability`. Precise,
   * and addressed to a developer rather than to the person who hit it.
   */
  required_capability?: string;
}

/**
 * A capability refusal, led by what the person was stopped from doing.
 *
 * Applied inside the ApiError constructor rather than at each render site,
 * because `err.message` is what roughly thirty screens already show. Fixing it
 * once here improves every one of them without touching a screen, and a screen
 * written tomorrow gets it for free — which is the opposite of the situation
 * this field was in, where each caller would have had to remember to read it.
 *
 * ADDITIVE, NEVER A REPLACEMENT. The server's detail already names the
 * capability and says to ask an owner or admin for a role that includes it —
 * measured on a live 403, after this was first written to replace it. Dropping
 * that would have traded the two useful halves of the message for a friendlier
 * tone. This prepends the missing half and keeps the rest, and falls through
 * untouched for a capability roles.ts does not know.
 */
function refusalMessage(problem: Problem): string | null {
  if (problem.status !== 403 || !problem.required_capability) return null;
  return explainRefusal(problem.required_capability, problem.detail);
}

/**
 * A validation failure, in the server's own words for each field.
 *
 * A 422's detail is only a summary — "One or more fields are invalid." — and the
 * reason is in `errors`, one per field: `"acme.test" is not a site address; give
 * one like https://www.example.com`. Screens show err.message, and all but one
 * of them read `errors` for nothing more than a red outline, so every field
 * refusal in the dashboard said that something was wrong and never what. Found on
 * the Forms screen's allowed-sites field. Same move as refusalMessage, for the
 * same reason: fixed once here rather than in each screen — Webhooks did it by
 * hand for its URL field, and nothing else copied it.
 */
function validationMessage(problem: Problem): string | null {
  const messages = (problem.errors ?? []).map((e) => e.message.trim()).filter(Boolean);
  if (messages.length > 1) {
    return messages.map((m) => (/[.!?]$/.test(m) ? m : `${m}.`)).join(" ");
  }
  return messages[0] ?? null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(refusalMessage(problem) ?? validationMessage(problem) ?? problem.detail ?? problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.problem = problem;
  }

  /**
   * The capability this refusal named, when it was one.
   *
   * For a caller that wants to show it separately from the sentence — an
   * administrator searches for `campaigns:send`, and the message is deliberately
   * written for the person who does not know that string.
   */
  get requiredCapability(): string | null {
    return this.problem.required_capability ?? null;
  }

  /** Field errors keyed by JSON pointer, for rendering next to an input. */
  get fields(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.field] = e.message;
    return out;
  }
}

/**
 * Thrown when the server refuses the credential.
 *
 * Separate from ApiError because it means something different to every caller:
 * not "this request failed" but "you are no longer signed in", which the shell
 * handles once rather than each screen handling it badly.
 */
export class UnauthenticatedError extends ApiError {
  constructor(problem: Problem) {
    super(problem);
    this.name = "UnauthenticatedError";
  }
}

/** Selects which organization a session is acting as. Ignored for API keys. */
const ORG_HEADER = "X-OpenComms-Org";

let activeOrgId: string | null = null;

/**
 * Sets the organization every subsequent request acts in.
 *
 * Module state rather than a React context value threaded through each call:
 * the header is required on every /v1 request, and something that must never be
 * omitted should not be something each caller passes.
 */
export function setActiveOrg(id: string | null): void {
  activeOrgId = id;
}

export function getActiveOrg(): string | null {
  return activeOrgId;
}

/**
 * The organization header, for the requests that cannot go through `request`.
 *
 * A multipart upload has to bypass the client: the browser must set the
 * boundary itself and will not do that if a Content-Type is supplied. The
 * header the client would have added therefore has to come from somewhere, and
 * it should be here rather than copied into each uploader — the whole point of
 * this module is that a screen cannot forget one of these.
 */
export function orgHeader(): Record<string, string> {
  return activeOrgId ? { [ORG_HEADER]: activeOrgId } : {};
}

/**
 * Downloads a file from a scoped endpoint.
 *
 * AN `<a href>` CANNOT DO THIS, and the contact export shipped as one for
 * months. A plain browser navigation sends no custom headers, so the request
 * arrives without `X-OpenComms-Org` and the session middleware — which reads
 * that header and nothing else — answers 401. The link looked right, the
 * endpoint worked, and clicking it produced a page of JSON error instead of a
 * file. Measured, not guessed: `fetch('/v1/contacts/export')` without the
 * header returns 401 against a live session.
 *
 * A query parameter would be the smaller change and the wrong one: the
 * organization a session is acting as is authorization input, and putting it in
 * a URL puts it in browser history, in the referrer of anything the response
 * links to, and in every access log between here and the server.
 *
 * So the bytes are fetched with the header and handed to the browser as a blob.
 * The object URL is revoked immediately after the click — it pins the whole
 * response in memory until it is, and an export is the one response likely to
 * be large.
 */
export async function download(path: string, filename: string): Promise<void> {
  const response = await fetch(new URL(path, window.location.origin), {
    headers: { ...orgHeader() },
    credentials: "same-origin",
  });
  if (!response.ok) throw await toError(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** Endpoints outside /v1 — the session and login flows — have no org scope. */
  unscoped?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal, unscoped = false } = options;

  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (!unscoped && activeOrgId) headers[ORG_HEADER] = activeOrgId;

  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    // Same-origin by default, but stated: the session cookie is the credential
    // and a change of default would log everyone out mysteriously.
    credentials: "same-origin",
    signal,
  });

  if (response.status === 204) return undefined as T;

  if (!response.ok) throw await toError(response);

  return (await response.json()) as T;
}

async function toError(response: Response): Promise<ApiError> {
  const problem = await readProblem(response);
  return response.status === 401 ? new UnauthenticatedError(problem) : new ApiError(problem);
}

/**
 * Reads the error envelope, tolerating responses that are not one.
 *
 * A proxy timing out, or a crash before the handler ran, produces HTML or
 * nothing at all. Those still have to surface as a readable message rather than
 * a JSON parse error that hides what actually happened.
 */
async function readProblem(response: Response): Promise<Problem> {
  const fallback: Problem = {
    type: "about:blank",
    title: response.statusText || "Request failed",
    status: response.status,
  };

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("json")) return fallback;

  try {
    const parsed = (await response.json()) as Partial<Problem>;
    return {
      ...fallback,
      ...parsed,
      // The server repeats the status in the body; trust the real one.
      status: response.status,
    };
  } catch {
    return fallback;
  }
}

/** Convenience wrappers. */
export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...options, method: "GET" }),

  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "POST", body }),

  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "PATCH", body }),

  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "PUT", body }),

  delete: <T = void>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...options, method: "DELETE" }),
};

/** An empty page, for rendering before the first response arrives. */
export function emptyPage<T>(): CursorPage<T> {
  return { data: [], next_cursor: null, has_more: false };
}
